import uuid from 'uuid/v4'
import { getCurrentPage } from 'tools/routes'
import { currentTimeInMs } from 'tools/date'
import IconSocialMedia from 'components/Icons/IconSocialMedia'
import IconText from 'components/Icons/IconText'
import IconNewsletter from 'components/Icons/IconNewsletter'

export const syncPrewiewUITypes = {
  SCROLL_INTO_VIEW: 'SCROLL_INTO_VIEW',
  CHANGE_SECTION_SETTING_STEP: 'CHANGE_SECTION_SETTING_STEP'
}

export const getPageSections = ({ query, sectionSettingData }) => {
  const currentPage = getCurrentPage(query)
  let pageSections = sectionSettingData.pages[currentPage] && sectionSettingData.pages[currentPage].sections
  while (!Array.isArray(pageSections)) {
    if (sectionSettingData.pages[pageSections] === undefined) break
    pageSections = sectionSettingData.pages[pageSections].sections
  }
  if (!pageSections) return [] // TODO: Throw error to signal 404
  return pageSections.map(
    section => ({ ...sectionSettingData.sections[section], key: section })
  )
}

export const getPagePopups = ({ query, sectionSettingData }) => {
  const currentPage = getCurrentPage(query)
  let pagePopups = sectionSettingData.pages[currentPage] && sectionSettingData.pages[currentPage].popups
  if (!pagePopups) return []
  return pagePopups.map(
    section => ({ ...sectionSettingData.sections[section], key: section })
  )
}

export const getSectionsFromIds = ({ sectionIds, sectionSettingData }) => {
  return sectionIds.map(
    sectionId => ({ ...sectionSettingData.sections[sectionId], key: sectionId })
  )
}

export const getSectionFromId = ({ sectionId, sectionSettingData }) => sectionSettingData.sections[sectionId] || {}

export const getLastSectionFromType = ({ type, sectionSettingData: { sections } }) => {
  const sectionKey = Object.keys(sections)
    .filter(
      (key) => (sections[key] || {}).type === type
    )
    .pop() // Get only the last section
  return sections[sectionKey] || {}
}

export const getPageOptions = ({ query, sectionSettingData }) => {
  const currentPage = getCurrentPage(query)
  return (sectionSettingData.pages[currentPage] && sectionSettingData.pages[currentPage].options) || {}
}

export const getPageData = ({ query, sectionSettingData }) => {
  const currentPage = getCurrentPage(query)
  return sectionSettingData.pages[currentPage]
}

export const makeNewSectionFromSchema = ({ sectionSchema, selectedPresetData }) => {
  const newId = `${sectionSchema.type}_${uuid()}`
  let sectionSettings = sectionSchema.presets.settings
  let presetBlocks // block setting from prest.

  // If there is on a preset
  if (selectedPresetData) {
    // Get preset section.
    const presetSection = getPresetSection({ preset: selectedPresetData, sectionType: sectionSchema.type })
    if (presetSection) {
      // Order of settings will apply is preset settings > section preset settings
      sectionSettings = { ...sectionSettings, ...presetSection.settings }
      // Get block setting from preset.
      presetBlocks = presetSection.blocks
    }
  }

  // Get section block defaul settings
  const sectionBlock = {}
  if (sectionSchema.presets.blocks) {
    sectionSchema.presets.blocks.forEach(block => {
      const blockSchema = sectionSchema.blocks.find(e => e.type === block.type)
      const { blockId, blockData } = makeNewBlockFromSchema({ blockSchema, presetBlocks })
      // Order of settings will apply is section preset settings > preset settings > block default settings
      sectionBlock[blockId] = { ...blockData, settings: { ...blockData.settings, ...block.settings } }
    })
  }

  return {
    sectionId: newId,
    sectionData: {
      id: newId,
      name: sectionSchema.name,
      type: sectionSchema.type,
      settings: sectionSettings,
      blocks: sectionBlock,
      blocksOrder: Object.keys(sectionBlock),
      createdAt: currentTimeInMs(),
      updatedAt: currentTimeInMs()
    }
  }
}

export const makeNewBlockFromSchema = ({ blockSchema, presetBlocks }) => {
  const newId = `${blockSchema.type}_${uuid()}`
  let blockSettings = blockSchema.settings.reduce((acc, { id, defaultValue }) => {
    acc[id] = defaultValue
    return acc
  }, {})

  // If there is on a preset
  if (presetBlocks) {
    const presetBlock = presetBlocks[blockSchema.type]
    if (presetBlock) {
      // Order will apply is preset settings > default settings.
      blockSettings = { ...blockSettings, ...presetBlock.settings }
    }
  }

  return {
    blockId: newId,
    blockData: {
      id: newId,
      name: blockSchema.name,
      type: blockSchema.type,
      settings: blockSettings
    }
  }
}

export const getBlocksDataFromSectionData = ({ blocks = {}, blocksOrder = [] }) =>
  blocksOrder.map((blockId) => blocks[blockId])

export const getBlockNameFromSectionSchema = ({ options = {} }) => {
  const { blockName = {} } = options
  const { singular = 'block', plural = 'blocks' } = blockName
  return { blockNameSingular: singular, blockNamePlural: plural }
}

export const canAddNewBlock = ({ blocksData, blockSchema }) => {
  const { options, type } = blockSchema
  if (!options || !options.maxItems) return true
  const numberOfBlocks = blocksData.filter(block => block.type === type).length
  return numberOfBlocks < options.maxItems
}

export const getBlockSchema = ({ blockType, sectionSchema }) => sectionSchema.blocks.find(({ type }) => type === blockType)

// Note: This method is different from getPageSections in that it also includes header/footer/popups if applicable
export const getAllSectionsAndPopupsInPage = ({ query, sectionSettingData }) => {
  let sections = [...getPageSections({ query, sectionSettingData }), ...getPagePopups({ query, sectionSettingData })]
  const pageOptions = getPageOptions({ query, sectionSettingData })
  if (!pageOptions.withoutHeader) {
    const headerSection = { ...getSectionFromId({ sectionId: 'header', sectionSettingData }), key: 'header' }
    sections = [headerSection].concat(sections)
  }
  if (!pageOptions.withoutFooter) {
    const footerSection = { ...getSectionFromId({ sectionId: 'footer', sectionSettingData }), key: 'footer' }
    sections = sections.concat([footerSection])
  }
  return sections
}

export const isPopupSection = (sectionSchema = {}) => {
  const { options = {} } = sectionSchema
  return options.isPopup
}

export const checkSectionLimitForPage = ({ query, sectionSchema, sectionSettingData }) => {
  const pagePopups = getPagePopups({ query, sectionSettingData })
  const pageSections = getPageSections({ query, sectionSettingData })
  const { options = {}, type: sectionType } = sectionSchema
  const sections = isPopupSection(sectionSchema) ? pagePopups : pageSections
  const sectionCount = sections.filter(({ type }) => sectionType === type).length

  if (options.maxPerPage !== undefined && sectionCount >= options.maxPerPage) {
    return false
  }

  return true
}

export const shouldShowSettingGroup = ({ values = {}, options = {} }) => options.showCondition
  ? Array.isArray(options.showCondition.values)
    ? options.showCondition.values.includes(values[options.showCondition.key])
    : values[options.showCondition.key] === options.showCondition.value
  : true

export const getPresetSection = ({ preset, sectionType }) => {
  if (!preset) return null
  const sections = preset.sections
  if (sections.hasOwnProperty(sectionType)) {
    return sections[sectionType]
  } else {
    return null
  }
}

export const getPresetBlock = ({ presetSection, blockType }) => {
  const blocks = presetSection.blocks
  if (blocks.hasOwnProperty(blockType)) {
    return blocks[blockType]
  } else {
    return null
  }
}

export const getPresetById = ({ presets, id }) => {
  const preset = presets.find((preset) => preset.id === id)
  return preset || null
}

export const getBlockIcon = ({ blockSchema }) => {
  const { options } = blockSchema
  if (!options || !options.icon) return null
  switch (options.icon) {
    case 'newsletterIcon':
      return IconNewsletter
    case 'textIcon':
      return IconText
    case 'socialMediaIcon':
      return IconSocialMedia
    default:
      return null
  }
}
